import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import { LOCATION_TYPES } from "../../utils/location.types";
import {
  addLocation,
  updateLocation,
  fetchLocationById,
  fetchLocations,
} from "../../store/locationStore/locationThunk";
import { fetchAddresses } from "../../store/addressStore/addressThunk";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  code: yup.string().required("Code is required"),
  type: yup.string().required("Type is required"),
  addressId: yup.string().required("Account address is required"),
});

interface LocationFormProps {
  open: boolean;
  onClose: (refresh: boolean) => void;
  locationId?: string;
}

// Right-side drawer form (same shell as the Record Expense Payment drawer):
// sticky DialogTitle header, scrollable DialogContent, fixed DialogActions footer.
const LocationForm: FC<LocationFormProps> = ({ open, onClose, locationId }) => {
  const dispatch = useAppDispatch();
  const isEditing = !!locationId;

  const { status, locations } = useAppSelector((state) => state.locations);
  const addresses = useAppSelector((state) => state.addresses.addresses);
  const { selectedAccount } = useAppSelector((state) => state.auth);

  const [type, setType] = useState("");
  const [policy, setPolicy] = useState(""); // '' = use account default
  const [parentLocationId, setParentLocationId] = useState<string>("");
  const [addressId, setAddressId] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);

  const methods = useForm({ resolver: yupResolver(schema) });

  // options for the parent-location & account-address pickers
  useEffect(() => {
    if (!open) return;
    dispatch(fetchLocations());
    if (selectedAccount?._id) dispatch(fetchAddresses(selectedAccount._id));
  }, [dispatch, open, selectedAccount?._id]);

  // hydrate (edit) or reset (new) each time the drawer opens
  useEffect(() => {
    if (!open) return;
    if (isEditing && locationId) {
      dispatch(fetchLocationById(locationId))
        .unwrap()
        .then((data: any) => {
          methods.setValue("name", data.name);
          methods.setValue("code", data.code);
          methods.setValue("type", data.type);
          setType(data.type || "");
          setPolicy(data.negativeStockPolicy || "");
          setParentLocationId(data.parentLocationId?._id || data.parentLocationId || "");
          setAddressId(data.addressId?._id || data.addressId || "");
          methods.setValue("addressId", data.addressId?._id || data.addressId || "");
          setIsActive(data.isActive !== false);
          setIsDefault(!!data.isDefault);
        })
        .catch((e: any) => toast.error(e?.message || "Failed to load location"));
    } else {
      methods.reset({ name: "", code: "", type: "", addressId: "" });
      setType("");
      setPolicy("");
      setParentLocationId("");
      setAddressId("");
      setIsActive(true);
      setIsDefault(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId]);

  const onSubmit = async (data: any) => {
    const payload = {
      name: data.name,
      code: data.code,
      type: data.type,
      negativeStockPolicy: policy || null,
      parentLocationId: parentLocationId || null,
      addressId: addressId || null,
      isActive,
      isDefault,
    };
    try {
      if (isEditing && locationId) {
        await dispatch(updateLocation({ id: locationId, locationPayload: payload })).unwrap();
      } else {
        await dispatch(addLocation(payload)).unwrap();
      }
      onClose(true);
    } catch (e: any) {
      toast.error(e?.message || "Failed to save location");
    }
  };

  const onError = (errors: any) => {
    const first = Object.values(errors)[0] as any;
    if (first?.message) toast.error(String(first.message));
  };

  const handleClose = () => {
    methods.reset();
    onClose(false);
  };

  const parentOptions = (locations || [])
    .filter((l) => l._id !== locationId)
    .map((l) => ({ label: `${l.name} (${l.code})`, value: l._id }));

  const addressOptions = (addresses || []).map((a: any) => {
    const title = a.title || "";
    const rest = `${[a.line1, a.city, a.state?.name].filter(Boolean).join(", ")}${a.code ? ` - ${a.code}` : ""} (${a.type || "Address"})`;
    return {
      value: a._id,
      title,
      rest,
      label: title ? `${title}, ${rest}` : rest, // shown in the input + used for filtering
    };
  });

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "560px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          padding: "2px 0 0 2px",
          position: "relative",
        }}
        component="form"
        onSubmit={methods.handleSubmit(onSubmit, onError)}
        noValidate
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "40px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
              {isEditing ? "Edit Location" : "New Location"}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 24px 100px", overflowY: "auto", flex: 1 }}>
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="Name *" fullWidth size="small" InputLabelProps={{ shrink: true }} {...methods.register("name")} />
              <p style={{ color: "#c33b3b", margin: "2px 0 0" }}>{methods.formState.errors.name?.message as string}</p>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Code *" fullWidth size="small" InputLabelProps={{ shrink: true }} {...methods.register("code")} />
              <p style={{ color: "#c33b3b", margin: "2px 0 0" }}>{methods.formState.errors.code?.message as string}</p>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Type *"
                fullWidth
                size="small"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  methods.setValue("type", e.target.value);
                }}
              >
                {LOCATION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
              <p style={{ color: "#c33b3b", margin: "2px 0 0" }}>{methods.formState.errors.type?.message as string}</p>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Negative-stock policy"
                fullWidth
                size="small"
                value={policy}
                onChange={(e) => setPolicy(e.target.value)}
              >
                <MenuItem value="allow_and_flag">Allow and Flag</MenuItem>
                <MenuItem value="block">Block</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={parentOptions}
                getOptionLabel={(o: any) => o.label || ""}
                value={parentOptions.find((o) => o.value === parentLocationId) || null}
                onChange={(_e, v: any) => setParentLocationId(v ? v.value : "")}
                renderInput={(params) => <TextField {...params} label="Parent location (optional)" size="small" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
              <FormControlLabel
                control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                label="Default"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={addressOptions}
                getOptionLabel={(o: any) => o.label || ""}
                value={addressOptions.find((o) => o.value === addressId) || null}
                onChange={(_e, v: any) => {
                  const val = v ? v.value : "";
                  setAddressId(val);
                  methods.setValue("addressId", val, { shouldValidate: true });
                }}
                renderOption={(props, o: any) => (
                  <li {...props} key={o.value}>
                    <span>
                      {o.title && <b>{o.title}</b>}
                      {o.title ? ", " : ""}
                      {o.rest}
                    </span>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Account address" size="small" fullWidth required />}
              />
              <p style={{ color: "#c33b3b", margin: "2px 0 0" }}>{methods.formState.errors.addressId?.message as string}</p>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #c1c5cb",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            padding: "16px 24px",
            margin: 0,
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, width: "100%" }}>
            <Button variant="outlined" onClick={handleClose} sx={{ minWidth: "100px", height: "40px" }}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{ minWidth: "140px", height: "40px" }}
            >
              {isEditing ? "Update" : "Create"}
            </LoadingButton>
          </Box>
        </DialogActions>
      </Box>
    </div>
  );
};

export default LocationForm;
